import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import { Box, Chip, Link, Typography } from "@mui/material";
import React, { useId, useState } from "react";
import {
  AssessmentQueryType,
  CompetitiveAssessment,
  CompetitorAnnouncement,
  getAssessmentSourceUrl
} from "./competitiveAssessmentUtils";
import { appInsights } from "../../../AppInsights";
import vendorLogoCatalog from "../../../images/vendor-logos.json";

const labelStyles = {
  color: "#0067C0",
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase"
} as const;

const bodyStyles = { color: "#334B66", fontSize: 14, lineHeight: 1.65, whiteSpace: "pre-wrap" } as const;
const getGridStyles = (itemCount: number, desktopColumns: number) => ({
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: `repeat(${Math.max(1, Math.min(itemCount, 2))}, minmax(0, 1fr))`,
    lg: `repeat(${Math.max(1, Math.min(itemCount, desktopColumns))}, minmax(0, 1fr))`
  },
  gap: 1.5
});
const cardStyles = {
  minWidth: 0,
  p: 1.75,
  border: "1px solid #DFE9F3",
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  overflowWrap: "anywhere"
};

const SectionHeading: React.FC<{ title: string; caption: string }> = ({ title, caption }) => (
  <Box
    sx={{ mb: 1, display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}
  >
    <Typography
      component="h3"
      sx={{ color: "#092C4C", fontSize: 20, fontWeight: 800, borderLeft: "4px solid #0078D4", pl: 1.25 }}
    >
      {title}
    </Typography>
    <Typography sx={{ color: "#61738A", fontSize: 13 }}>{caption}</Typography>
  </Box>
);

const vendorLogos = new Map(vendorLogoCatalog.map((logo) => [logo.vendor.toLowerCase(), logo]));

const VendorMark: React.FC<{ vendor: string }> = ({ vendor }) => {
  const logo = vendorLogos.get(vendor.trim().replace(/\s+/g, " ").toLowerCase());
  const [failedLogo, setFailedLogo] = useState<string>();
  const showLogo = logo && logo.vendor !== failedLogo;

  return (
    <Box
      aria-hidden="true"
      title={logo && !showLogo ? `${vendor} logo unavailable; showing initials.` : undefined}
      sx={{
        display: "grid",
        placeItems: "center",
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: "50%",
        border: "1px solid #C5E3FF",
        bgcolor: showLogo ? "#FFFFFF" : "#D2E9FC",
        color: "#0F6CBD",
        fontSize: 20,
        fontWeight: 800
      }}
    >
      {showLogo ? (
        <Box
          component="img"
          src={logo.dataUrl}
          alt=""
          width={32}
          height={32}
          sx={{ objectFit: "contain" }}
          onError={() => {
            setFailedLogo(logo.vendor);
            appInsights.trackEvent({
              name: "CompetitiveVendorLogoLoadFailed",
              properties: { component: "CompetitiveOpportunitiesContent", vendor: logo.vendor }
            });
          }}
        />
      ) : (
        vendor
          .split(/\s+/)
          .filter(Boolean)
          .map((part) => part[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      )}
    </Box>
  );
};

const SnapshotSummary: React.FC<{ summary: string; vendor: string }> = ({ summary, vendor }) => {
  const [expanded, setExpanded] = useState(false);
  const summaryId = useId();
  const limit = 220;
  const isLong = summary.length > limit;
  const preview = summary.slice(0, limit).replace(/\s+\S*$/, "");

  return (
    <Box sx={{ mt: 0.25, mb: 1.75 }}>
      <Typography component="span" id={summaryId} sx={bodyStyles}>
        {summary ? (isLong && !expanded ? `${preview}...` : summary) : "No summary is available."}
      </Typography>
      {isLong && (
        <>
          {" "}
          <Link
            component="button"
            type="button"
            aria-expanded={expanded}
            aria-controls={summaryId}
            aria-label={`${expanded ? "Read less" : "Read more"} about ${vendor}`}
            onClick={() => setExpanded((current) => !current)}
            sx={{ fontSize: 14, verticalAlign: "baseline" }}
          >
            {expanded ? "Read less" : "Read more"}
          </Link>
        </>
      )}
    </Box>
  );
};

const Announcement: React.FC<{ announcement: CompetitorAnnouncement }> = ({ announcement }) => {
  const url = getAssessmentSourceUrl(announcement.url);
  // Only use an explicit maturity annotation, not incidental mentions of GA or preview.
  const maturity = announcement.description.match(/\bMaturity:\s*([^.\n]+)/i)?.[1] || "";
  const statuses = [
    ...(/\bpublic preview\b/i.test(maturity) ? ["Public Preview"] : []),
    ...(/\bGA\b|\bgenerally available\b|\bgeneral availability\b/i.test(maturity) ? ["GA"] : [])
  ];

  return (
    <Box sx={{ py: 1, "& + &": { borderTop: "1px solid #DFE9F3" } }}>
      {announcement.title && (
        <Typography sx={{ ...bodyStyles, fontWeight: 700 }}>
          {url ? (
            <Link href={url} target="_blank" rel="noopener noreferrer">
              {announcement.title}
            </Link>
          ) : (
            announcement.title
          )}
        </Typography>
      )}
      {announcement.date && <Typography sx={{ color: "#61738A", fontSize: 12 }}>{announcement.date}</Typography>}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
        {(statuses.length ? statuses : ["Unverified"]).map((status) => (
          <Chip
            key={status}
            label={status}
            size="small"
            title={
              status === "Unverified"
                ? "No recognized maturity annotation was supplied."
                : `Reported maturity: ${maturity}`
            }
            sx={{
              height: 20,
              fontSize: 12,
              bgcolor: status === "GA" ? "#B9ECCC" : status === "Public Preview" ? "#FFE3A5" : "#E5EAF0",
              color: status === "GA" ? "#155D34" : status === "Public Preview" ? "#864A00" : "#52677E"
            }}
          />
        ))}
      </Box>
      {announcement.description && (
        <Box component="details" sx={{ mt: 0.5 }}>
          <Box component="summary" sx={{ color: "#0067C0", fontSize: 13, cursor: "pointer" }}>
            Announcement details
          </Box>
          <Typography sx={bodyStyles}>{announcement.description}</Typography>
        </Box>
      )}
      {announcement.url && !url && (
        <Typography sx={{ ...bodyStyles, color: "#616161" }}>Source link unavailable.</Typography>
      )}
      {url && !announcement.title && (
        <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 11 }}>
          View announcement source
        </Link>
      )}
    </Box>
  );
};

const CompetitiveOpportunitiesContent: React.FC<{
  assessment: CompetitiveAssessment;
  queryType: AssessmentQueryType;
}> = ({ assessment, queryType }) => (
  <>
    <Typography sx={{ ...bodyStyles, mb: 2.5 }}>{assessment.executiveSummary || "No summary is available."}</Typography>
    {queryType === "TopCustomerNeedsCompetitiveAssessment" && (
      <Box component="section" aria-label="Top Opportunities">
        <SectionHeading
          title="Top Opportunities"
          caption="Focused on access, trustworthy closure, and operational relevance"
        />
        <Box sx={getGridStyles(assessment.topOpportunities?.length ?? 0, 3)}>
          {assessment.topOpportunities?.length ? (
            assessment.topOpportunities.map((opportunity, index) => (
              <Box component="article" key={index} sx={{ ...cardStyles, borderTop: "4px solid #0078D4" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "50%",
                      bgcolor: "#0F6CBD",
                      color: "#FFFFFF",
                      fontSize: 20,
                      fontWeight: 800
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography component="h4" sx={{ minWidth: 0, color: "#092C4C", fontSize: 15, fontWeight: 800 }}>
                    {opportunity.title}
                  </Typography>
                </Box>
                <Typography sx={bodyStyles}>{opportunity.description}</Typography>
                <Box
                  sx={{
                    mt: 1.25,
                    p: 1.5,
                    borderRadius: "4px",
                    bgcolor: "#EDF5FD"
                  }}
                >
                  <Typography sx={labelStyles}>Expected Impact</Typography>
                  <Typography sx={bodyStyles}>
                    {opportunity.expectedImpact || "No expected impact is available."}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1.75, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
                  <Typography sx={{ ...labelStyles, mr: 0.75 }}>Competitive Pressure</Typography>
                  {opportunity.competitivePressure.length ? (
                    opportunity.competitivePressure.map((competitor, competitorIndex) => (
                      <Chip
                        key={competitorIndex}
                        label={competitor.vendor}
                        title={competitor.productOrSurface}
                        size="small"
                        variant="outlined"
                        sx={{
                          maxWidth: "100%",
                          height: "auto",
                          minHeight: 22,
                          bgcolor: "#EDF5FD",
                          borderColor: "#C5E3FF",
                          color: "#092C4C",
                          fontSize: 12,
                          "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 }
                        }}
                      />
                    ))
                  ) : (
                    <Typography sx={bodyStyles}>No competitive pressure is available.</Typography>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={bodyStyles}>No top opportunities are available.</Typography>
          )}
        </Box>
      </Box>
    )}
    {queryType === "CompetitiveAssessment" && (
      <Box component="section" aria-label="Competitor Snapshots">
        <SectionHeading title="Competitor Snapshots" caption="Concise executive view with source-linked detail" />
        <Box sx={getGridStyles(assessment.competitorSnapshots?.length ?? 0, 4)}>
          {assessment.competitorSnapshots?.length ? (
            assessment.competitorSnapshots.map((competitor, index) => {
              return (
                <Box component="article" key={index} sx={{ ...cardStyles, display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      minHeight: 56,
                      mb: 1.5
                    }}
                  >
                    <VendorMark vendor={competitor.vendor} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="h4" sx={{ color: "#092C4C", fontSize: 15, fontWeight: 800 }}>
                        {competitor.vendor}
                      </Typography>
                      <Typography sx={{ ...bodyStyles, fontSize: 13 }}>{competitor.productOrSurface}</Typography>
                    </Box>
                  </Box>
                  <Typography sx={labelStyles}>Summary</Typography>
                  <SnapshotSummary key={competitor.summary} summary={competitor.summary} vendor={competitor.vendor} />
                  <Box
                    sx={{ borderTop: "1px solid #DFE9F3", pt: 1.5, display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <Typography sx={labelStyles}>Recent Announcements</Typography>
                    {competitor.announcements.length ? (
                      competitor.announcements.map((announcement, announcementIndex) => (
                        <Announcement key={announcementIndex} announcement={announcement} />
                      ))
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 140,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          py: 2
                        }}
                      >
                        <DescriptionOutlined aria-hidden="true" sx={{ color: "#94A8BF", fontSize: 44 }} />
                        <Typography sx={{ color: "#61738A", fontSize: 13, textAlign: "center" }}>
                          No recent announcements are available.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography sx={bodyStyles}>No competitor snapshots are available.</Typography>
          )}
        </Box>
      </Box>
    )}
  </>
);

export default CompetitiveOpportunitiesContent;
